import { useState, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';

export const useHcpLicensesListApproval = ({
  hcpLicensesListId,
  requestId,
  persona = null,
}) => {
  const checkLink = persona === 'enduser' ? 'check/enduser' : 'check';

  const [isApproved, setIsApproved] = useState(false);
  const [isCheckingApproval, setIsCheckingApproval] = useState(true);

  const [isApproving, setIsApproving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [approvalError, setApprovalError] = useState(null);

  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    const fetchApproval = async () => {
      if (!hcpLicensesListId || !requestId) {
        setIsCheckingApproval(false);
        setIsApproved(false);
        return;
      }

      setIsCheckingApproval(true);

      try {
        const response = await axiosInstance.get(
          `/api/approve-hcp-licenses-list/${checkLink}`,
          {
            params: {
              request_id: requestId,
              hcp_licenses_list_id: hcpLicensesListId,
            },
          }
        );

        const newApprovalStatus =
          response.data &&
          response.data.is_approved !== null &&
          response.data.is_approved !== undefined
            ? response.data.is_approved
            : false;

        setIsApproved((prev) =>
          prev === newApprovalStatus ? prev : newApprovalStatus
        );
      } catch (error) {
        console.error('error checking approval:', error);
        setIsApproved(false);
      } finally {
        setIsCheckingApproval(false);
      }
    };

    fetchApproval();

    const handleApprovalChange = (event) => {
      if (
        event.detail.hcpLicensesListId === hcpLicensesListId &&
        event.detail.requestId === requestId
      ) {
        setIsApproved((prev) => {
          if (prev !== event.detail.approved) {
            return event.detail.approved;
          }
          return prev;
        });
      }
    };

    window.addEventListener(
      'hcpLicensesListApprovalChanged',
      handleApprovalChange
    );

    return () => {
      window.removeEventListener(
        'hcpLicensesListApprovalChanged',
        handleApprovalChange
      );
    };
  }, [hcpLicensesListId, requestId, checkLink, refreshCounter]);

  const approveHcpLicensesList = async () => {
    if (!hcpLicensesListId || !requestId) {
      const error = new Error('something went wrong.');
      setApprovalError(error);
      console.error(error);
      return false;
    }

    setIsApproving(true);
    setApprovalError(null);

    try {
      const response = await axiosInstance.post(
        `/api/approve-hcp-licenses-list/approve`,
        {
          hcp_licenses_list_id: hcpLicensesListId,
          request_id: requestId,
        }
      );

      if (response.status === 200 || response.status === 201) {
        setIsApproved(true);
        window.dispatchEvent(
          new CustomEvent('hcpLicensesListApprovalChanged', {
            detail: { hcpLicensesListId, requestId, approved: true },
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      setApprovalError(error);
      return false;
    } finally {
      setIsApproving(false);
    }
  };

  const deleteApproval = async () => {
    if (!hcpLicensesListId || !requestId) {
      const error = new Error('something went wrong.');
      setApprovalError(error);
      console.error(error);
      return false;
    }

    setIsDeleting(true);
    setApprovalError(null);

    try {
      const response = await axiosInstance.delete(
        `/api/approve-hcp-licenses-list/delete`,
        {
          data: {
            hcp_licenses_list_id: hcpLicensesListId,
            request_id: requestId,
          },
        }
      );

      if (response.status === 200) {
        setIsApproved(false);
        window.dispatchEvent(
          new CustomEvent('hcpLicensesListApprovalChanged', {
            detail: { hcpLicensesListId, requestId, approved: false },
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      setApprovalError(error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    isApproved,
    isCheckingApproval,
    isApproving,
    isDeleting,
    approvalError,
    approveHcpLicensesList,
    deleteApproval,
  };
};
